import React, { useState, useMemo, useEffect } from 'react'; 
import { FavoriteBorder, Star, AddShoppingCart } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  // Slider removed
} from '@mui/material';
import axios from 'axios'; 
import { useShop } from '../context/ShopContext';

// 🛑 Bangles Banner Image URL 
const BANGLES_BANNER_IMAGE = 'https://cdnmedia-breeze.vaibhavjewellers.com/media/catalog/category/bangle-collections-web-cat-banners-vaibhavjewellers.jpg';


// ---
// Product Data & Filter Options 
// ---

// Unified Product type for display
interface Product {
  id: number; 
  imageUrl: string;
  name: string;
  price: number;
  rating: number; 
  // Fields needed for custom filtering in Bangles.tsx
  material: string; 
  style: string;
  occasion: string;
  color: string;
  
  // API category is stored for filtering purposes
  api_category: string; 
  api_stock: number;
}

const filterOptions = {
  material: ['Gold Plated', 'Silver Plated', 'Brass', 'Copper', 'Alloy'],
  style: ['Kada', 'Set of 2', 'Set of 4', 'Thin', 'Cuff'],
  occasion: ['Wedding', 'Party Wear', 'Everyday', 'Festive'],
  color: ['Gold', 'Silver', 'Rose Gold', 'Oxidised'],
};


// ---
// Product Card Component (Unchanged)
// ---


interface ProductCardProps {
  product: Product; 
  onAddToCart: (productId: number, productType: string, quantity: number, itemName: string) => Promise<void>; 
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  

 const handleAddToCart = () => {
        // CartContext में addItemToCart function को कॉल करें
        // Product Type को `stone_metal` से निकालें ('Gold' या 'Silver')
        const productType = product.material.toLowerCase().includes('silver') ? 'silver' : 'gold';

        onAddToCart(
            product.id,        // productId
            productType,       // productType ('gold' या 'silver')
            1,                 // quantity (डिफ़ॉल्ट 1)
            product.name       // itemName
        );
    };
  
  const handleAddToWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Added "${product.name}" to Wishlist! (Dummy Action)`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer">
      
      <div className="relative w-full h-72 overflow-hidden">
        <div className="w-full h-full">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
        </div>
        
        {/* <button
          className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md text-red-500 hover:text-red-700 transition"
          aria-label="Add to Wishlist"
          onClick={handleAddToWishlist}
        >
          <FavoriteBorder fontSize="small" />
        </button> */}
      </div>
      
      <div className="p-3 text-center">
        {/* Added Style/Material tag for context in Bangles page */}
        <span className="text-xs font-semibold uppercase text-amber-700 bg-amber-100 px-2 py-1 rounded mb-1 inline-block">
            {product.style} / {product.material}
        </span>
      
        <p className="block text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
          {product.name}
        </p>
        
        <p className="text-lg font-bold text-amber-900 mt-1">
          ₹{product.price.toLocaleString('en-IN')}
        </p>

        <div className="flex justify-center items-center mt-1">
          <Star sx={{ fontSize: 16, color: 'gold' }} />
          <span className="text-sm text-gray-600 ml-1">{product.rating.toFixed(1)}</span>
        </div>
        
        <button 
          className="mt-3 w-full py-2 flex items-center justify-center gap-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 transition"
          onClick={handleAddToCart}
        >
          <AddShoppingCart fontSize="small" />
          <span>Add To Cart</span>
        </button>
      </div>
    </div>
  );
};

// ---
// Filter Sidebar Component (Price Filter Removed)
// ---

const FilterSidebar: React.FC<{ 
  filters: typeof filterOptions,
  selectedFilters: Record<string, string[]>,
  onFilterChange: (category: string, value: string) => void,
}> = ({ filters, selectedFilters, onFilterChange }) => {

    // Helper for rendering checkboxes
    const renderCheckboxes = (category: keyof typeof filterOptions, title: string) => (
        <div className="mb-4">
            <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
            <List dense className="p-0">
                {filters[category].map((item) => (
                    <ListItem key={item} disablePadding className="py-0 min-h-[30px]">
                        <Checkbox
                            size="small"
                            checked={selectedFilters[category]?.includes(item) || false}
                            onChange={() => onFilterChange(category, item)}
                        />
                        <ListItemText primary={item} className="text-sm" />
                    </ListItem>
                ))}
            </List>
        </div>
    );
    

    return (
        <div className="hidden lg:block w-64 p-4 bg-gray-50 border-r border-gray-200 sticky top-0 h-full overflow-y-auto">
          <h3 className="text-xl font-bold text-amber-900 mb-4 border-b pb-2">Filters</h3>

          {/* Price Filter Removed */}
          
          {/* Material Filter */}
          {renderCheckboxes('material', 'Base Metal/Material')}
          
          {/* Style Filter */}
          {renderCheckboxes('style', 'Style')}

          {/* Occasion Filter */}
          {renderCheckboxes('occasion', 'Occasion')}
          
           {/* Color Filter */}
          {renderCheckboxes('color', 'Color')}
        </div>
    );
};


// ---
// Main Bangles Page Component (Price Filter Logic Removed)
// ---

const BanglesPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState<string | null>(null); 
  const [sortBy, setSortBy] = useState('latest');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});

   const { addItemToCart } = useShop();
  
  // Price range state and related functions removed


  // --- Data Fetching Logic (Unchanged) ---
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      // Helper function to map API response to the Product interface
      const mapApiItemToProduct = (item: any, metalType: string): Product => ({
          id: item.srno,
          imageUrl: item.images && item.images.length > 0
            ? item.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image', 
          name: item.itemName,
          price: item.price, 
          rating: item.rating || (metalType === 'Gold' ? 4.9 : 4.5), 
          
          // MOCKING/INFERRING FIELDS FOR CUSTOM FILTERS:
          material: metalType === 'Gold' ? 'Gold Plated' : 'Silver Plated',
          style: item.itemName.toLowerCase().includes('kada') ? 'Kada' : 'Set of 2', 
          occasion: item.itemName.toLowerCase().includes('bridal') ? 'Wedding' : 'Festive',
          color: metalType === 'Gold' ? 'Gold' : 'Silver',
          
          api_category: item.category,
          api_stock: item.stockQuantity,
      });

      try {
        let allFetchedProducts: Product[] = [];
  
        // 1. Fetch Gold Products
        // const goldResponse = await axios.get<any[]>('http://localhost:8080/api/admin/gold-products');
        const goldResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/gold-products');
        const goldProducts = goldResponse.data.map(item => mapApiItemToProduct(item, 'Gold'));
        allFetchedProducts = allFetchedProducts.concat(goldProducts);
  
        // 2. Fetch Silver Products
        const silverResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/silver-products');
        const silverProducts = silverResponse.data.map(item => mapApiItemToProduct(item, 'Silver'));
        allFetchedProducts = allFetchedProducts.concat(silverProducts);
  
        // 3. Filter the combined products to keep ONLY "Bangles" or "Kadas"
        const banglesOnly = allFetchedProducts.filter(product => 
            product.api_category?.toLowerCase() === 'bangles' || 
            product.name?.toLowerCase().includes('bangle') ||
            product.name?.toLowerCase().includes('kada')
        );
        
        setProducts(banglesOnly);

      } catch (err) {
        console.error('Error fetching bangles:', err);
        setError('Bangles load failed. Please check your backend server .');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); 

  // Price Change Handler removed


  // FILTERING LOGIC (Modified: Price Range filtering removed)
  const filteredProducts = useMemo(() => {
    let currentProducts = products; 
    
    // Price Range filter removed
    
    // Filter by Checkbox Categories
    Object.keys(selectedFilters).forEach(category => {
      const selectedValues = selectedFilters[category];
      
      if (selectedValues && selectedValues.length > 0) {
        currentProducts = currentProducts.filter(product => {
          return selectedValues.includes(product[category as keyof Product] as string);
        });
      }
    });

    return currentProducts;
  }, [products, selectedFilters]); 

  // SORTING LOGIC 
  const sortedAndFilteredProducts = useMemo(() => {
    const sorted = [...filteredProducts].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating-desc') return b.rating - a.rating;
      return a.id - b.id; 
    });
    return sorted;
  }, [filteredProducts, sortBy]);


  // FILTER CHANGE HANDLER 
  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      if (newValues.length === 0) {
        const { [category]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [category]: newValues };
    });
  };

  // --- Render Loading/Error States ---
  if (loading) {
    return <div className="text-center py-20 text-xl text-amber-800">Bangles are loading ...</div>;
  }

  if (error) {
    return <div className="text-center py-20 text-xl text-red-600">{error}</div>;
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      
      {/* 🛑 Banner Section with Background Image */}
     <div 
        className="w-full h-[300px] text-white p-4 py-6 md:py-8 lg:py-10 shadow-lg relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${BANGLES_BANNER_IMAGE})` }}
      >
        {/* Left side content */}
        {/* <div className="absolute top-1/2 left-8 transform -translate-y-1/2 max-w-md">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold font-[Cinzel] mb-3">
            Bangles & Kadas
            </h1>
            <p className="text-sm sm:text-base font-light mb-4">
            Wrap your wrists in elegance. Traditional designs to modern cuffs.
            </p>
            <button className="bg-amber-950 hover:bg-amber-800 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
            Shop Now
            </button>
        </div> */}
      </div>

      
      {/* Main Content Area: Sidebar + Products */}
      <div className="flex flex-1"> 
        
        {/* Sidebar - Filters */}
        <FilterSidebar 
          filters={filterOptions} 
          selectedFilters={selectedFilters}
          // priceRange prop removed
          onFilterChange={handleFilterChange}
          // onPriceChange prop removed
        />
        
        {/* Products Display */}
        <div className="flex-1 p-4 lg:p-8">
          
          {/* Header and Sort By */}
          <div className="flex justify-between items-center mb-6 border-b pb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-800">
                 Bangles 
            </h1>
            
            <FormControl variant="outlined" size="small" className="min-w-[120px] bg-white">
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as string)}
                label="Sort by"
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b45309 !important' }, 
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a16207 !important' }
                }} 
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="price-asc">Price: Low to High</MenuItem>
                <MenuItem value="price-desc">Price: High to Low</MenuItem>
                <MenuItem value="rating-desc">Rating</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {/* Products Grid */}
          {sortedAndFilteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {sortedAndFilteredProducts.map(product => (
                           <ProductCard 
        key={product.id} 
        product={product} 
        onAddToCart={addItemToCart} 
      />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-lg text-gray-600">
                No Bangles found matching your filters.
            </div>
          )}
          
        </div>
        
      </div>
      
    </div>
  );
};

export default BanglesPage;